import { handleCustomUDL } from "@utils/UDLUtils";
import Swiper from "swiper";
import { Pagination, A11y, Navigation } from 'swiper/modules';
import { Movie } from "@components/movie/Movie";

const constants = {
  youtubeDefaultSize: "mqdefault",
  youtubeSmallSize: "hqdefault",
  youtubeLargeSize: "maxresdefault",
  youtubeSmallSizeWidth: 480,
  vimeoDefaultSize: "295x166",
};

const attributes = {
  dataId: "data-t-id",
  movieType: "data-movie-type",
  thumbnailUrl: "data-thumbnail-url",
  altText: "data-alt-text",
  serverUrl: "data-serverurl",
  posterImage: "data-posterimage",
  columns: "data-columns",
};

const classnames = {
  testimonialOptimize: "testimonial-optimize",
  isAuthoring: "is-authoring",
};

const selectors = {
  movie: ".comp-movie",
  movieOverlay: ".comp-movie-overlay",
  movieOverlayPicture: ".comp-movie-overlay-picture",
  movieCanvas: ".movie-canvas",
  startMovieButton: ".start-video-button > button",
  jsViewer: ".js-video-viewer",
  testimonialColumns: ".testimonial__columns",
  compItem: ".comp-testimonial-item",
  authorName: ".testimonial-item__author-name",
  buttongroupButton: ".comp-buttongroup .button",
  paginationContainer: ".swiper-dots",
  swiperNextButton: ".swiper-next",
  swiperPrevButton: ".swiper-prev",
};

class Testimonial {
  private el: HTMLElement;
  private isAuthoring: boolean;
  private columnsWrapper: HTMLElement;
  private columnsCount: number;
  private movieOverlays: NodeListOf<HTMLElement>;
  private swiper: Swiper;
  private pagination: HTMLElement;
  private swiperNextButton: HTMLElement;
  private swiperPrevButton: HTMLElement;

  constructor(el: HTMLElement) {
    this.el = el;
    this.isAuthoring = this.el.classList.contains(classnames.isAuthoring);

    this.columnsWrapper = this.el.querySelector(selectors.testimonialColumns);
    this.columnsCount = parseInt(this.columnsWrapper.getAttribute(attributes.columns)) || 1;

    this.pagination = this.el.querySelector(selectors.paginationContainer);
    this.movieOverlays = this.el.querySelectorAll(selectors.movieOverlay);

    this.swiperNextButton = this.el.querySelector(selectors.swiperNextButton);
    this.swiperPrevButton = this.el.querySelector(selectors.swiperPrevButton);

    if (this.movieOverlays.length > 0) {
      this.movieOverlays.forEach((movieOverlay) => {
        this.appendImagePlaceholderElement(movieOverlay);
        this.handleMovieOverlay(movieOverlay);
      });
    }
  }

  public init() {
    this.initSwiper();
    this.addAnalyticsListeners();
  }

  private initSwiper = () => {
    if (this.isAuthoring) {
      // In authoring mode, we do not initialize swiper
      return;
    }

    this.swiper = new Swiper(this.el, {
      loop: false,
      allowTouchMove: true,
      modules: [Pagination, A11y, Navigation],
      speed: 200,
      pagination: {
        el: this.pagination,
        type: "bullets",
        clickable: true,
        bulletClass: "swiper-bullet",
        bulletActiveClass: "swiper-bullet-active",
      },
      slidesPerView: 1,
      slidesPerGroup: 1,
      navigation: {
        nextEl: this.swiperNextButton,
        prevEl: this.swiperPrevButton,
        disabledClass: "swiper-disabled"
      },
      breakpoints: {
        // Desktop and above
        1025: {
          slidesPerView: this.columnsCount,
          slidesPerGroup: this.columnsCount,
          pagination: {
            el: this.pagination,
            type: "bullets",
            clickable: true,
          }
        }
      }
    });
  }

  private handleMovieOverlay = (movieOverlay: HTMLElement) => {
    const movie = movieOverlay?.querySelector<HTMLElement>(selectors.movie);
    const movieCanvas = movie?.querySelector(selectors.movieCanvas);

    movieCanvas?.setAttribute("data-auto-play", "false");

    const openModal = () => {
      movieCanvas?.setAttribute("data-auto-play", "true");

      window.Lightbox?.openModal(movie);

      window.setTimeout(() => {
        if (movie) {
          Movie.initOnElement(movie);
        }
      }, 100);
    }

    movieOverlay?.addEventListener("click", () => openModal());
    movieOverlay?.addEventListener("keydown", (e) => {
      if (e.code === "Enter" || e.code === "Space") {
        e.preventDefault();
        openModal();
      }
    });

    const item = movieOverlay?.closest(selectors.movieOverlay);
    const startMovieButton = item?.querySelector(selectors.startMovieButton);

    startMovieButton?.addEventListener("click", () => movieOverlay.click());
  }

  private appendImagePlaceholderElement = (testimonialItem: HTMLElement) => {
    const imagePlaceholder = testimonialItem.querySelector<HTMLElement>(
      selectors.movieOverlayPicture,
    );
    const compMovie = testimonialItem.querySelector<HTMLElement>(selectors.movie);
    const movieCanvas = compMovie?.querySelector<HTMLElement>(selectors.movieCanvas);
    const mediaType = movieCanvas?.getAttribute(attributes.movieType);
    const compMovieId = compMovie?.getAttribute(attributes.dataId);
    let thumbnailUrl = movieCanvas?.getAttribute(attributes.thumbnailUrl);
    const altText = testimonialItem.getAttribute(attributes.altText) ?? "";

    if (mediaType === "dynamicmedia") {
      const jsViewer = compMovie?.querySelector<HTMLElement>(selectors.jsViewer);
      const serverUrl = jsViewer?.getAttribute(attributes.serverUrl);
      const posterImage = jsViewer?.getAttribute(attributes.posterImage);

      if (serverUrl?.length > 0 && posterImage?.length > 0) {
        thumbnailUrl = `${serverUrl}${posterImage}`;
      }
    }

    if (mediaType === "youtube") {
      // youtube default thumbnail file name is 'mqdefault'
      // we need to replace it with the proper size
      thumbnailUrl = thumbnailUrl?.replace(
        /mqdefault/g,
        this.getYoutubeImageSize(testimonialItem),
      );
    }

    if (mediaType === "vimeo") {
      // vimeo default thumbnail size is 295x166
      // we need to replace it with the size of the placeholder
      // to get the correct aspect ratio and size
      thumbnailUrl = thumbnailUrl?.replace(
        /295x166/g,
        this.getVimeoImageSize(testimonialItem),
      );
    }

    const img = document.createElement("img");
    img.id = `image-placeholder-${compMovieId}`;
    img.src = thumbnailUrl ?? "";
    img.alt = altText;
    img.loading = "lazy";
    imagePlaceholder?.appendChild(img);
  }

  private getYoutubeImageSize = (singleQuote: HTMLElement) => {
    const placeholderWidth = singleQuote.getBoundingClientRect().width;

    return placeholderWidth <= constants.youtubeSmallSizeWidth
      ? constants.youtubeSmallSize
      : constants.youtubeLargeSize;
  }

  private getVimeoImageSize = (singleQuote: HTMLElement) => {
    const placeholderWidth = singleQuote.getBoundingClientRect().width;
    const placeholderHeight = (placeholderWidth * 9) / 16;

    return `${placeholderWidth.toFixed()}x${placeholderHeight.toFixed()}`;
  }

  private addAnalyticsListeners = () => {
    const buttons = this.el.querySelectorAll(selectors.buttongroupButton);

    buttons.forEach(button => {
      button.addEventListener("click", (e) => {
        const target = e?.currentTarget as HTMLLinkElement;
        const item = target?.closest<HTMLElement>(selectors.compItem);
        const author = item?.querySelector<HTMLElement>(selectors.authorName);

        const data = {
          author_name: author?.innerText || "",
          cta_text: target?.innerText || "",
          page_path: target?.href || "",
        };

        this.pushData(data);
      });
    });
  }

  private pushData = (params) => {
    handleCustomUDL("quotes_testimonial_cta_click", params);
  }
}

const components = {
  "testimonial": Testimonial
};

export function initComponents(root = document) {
  Object.entries(components).forEach(([name, Component]) => {
    root.querySelectorAll<HTMLElement>(`[data-component="${name}"]`).forEach(el => {
      new Component(el).init();
    });
  });
}

if (document.readyState !== "loading") {
  initComponents();
} else {
  document.addEventListener("DOMContentLoaded", () => initComponents());
}